import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

// Which model runs which pipeline surface. The server resolves the whole map
// (personal pick > env config > built-in default) at GET /api/pipeline/models;
// every panel reads that same map, so a label on screen always matches the
// call that actually runs. Picking a model here PUTs it back and it sticks to
// the account, not the browser.

let cache = null;      // { base, options, list, byKey }
let inflight = null;
const listeners = new Set();   // components to refresh after a pick

function shape(data) {
  const list = (data && Array.isArray(data.models)) ? data.models : [];
  const byKey = {};
  list.forEach((e) => { byKey[e.key] = e; });
  const or = (data && data.openrouter) || {};
  return {
    base: (data && data.base) || null,
    options: (data && Array.isArray(data.options)) ? data.options : [],
    openrouter: {
      options: Array.isArray(or.options) ? or.options : [],
      keySet: !!or.keySet,
      tier: or.tier || null,
      pin: or.pin || null,
    },
    // ChatGPT subscription through the OpenCode CLI (ids "chatgpt/<model>").
    chatgpt: {
      options: (data && data.chatgpt && Array.isArray(data.chatgpt.options)) ? data.chatgpt.options : [],
      binSet: !!(data && data.chatgpt && data.chatgpt.binSet),
    },
    list,
    byKey,
  };
}

function publish(next) {
  cache = next;
  listeners.forEach((fn) => fn(next));
  return next;
}

export function fetchModels() {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = fetch('/api/pipeline/models', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => publish(shape(d)))
      .catch(() => shape(null))          // never break a panel over a label
      .finally(() => { inflight = null; });
  }
  return inflight;
}

// Pick a model for one surface (null/'' returns it to the server default).
// Resolves to the fresh map; every mounted picker updates with it.
export async function saveModelPick(key, model) {
  const res = await fetch('/api/pipeline/models', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ models: { [key]: model || null } }),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error((data && data.error) || `HTTP ${res.status}`);
  return publish(shape(data));
}

// Returns null until loaded, then { base, options, list, byKey }.
export function useModels() {
  const [models, setModels] = useState(cache);
  useEffect(() => {
    listeners.add(setModels);
    if (!cache) fetchModels().then((m) => setModels(m));
    return () => { listeners.delete(setModels); };
  }, []);
  return models;
}

// One surface's model id, or '' while the map is still loading.
export const modelKey = (models, key) => (
  (models && models.byKey[key] && models.byKey[key].model) || ''
);

// 'claude-opus-4-6' -> 'opus-4-6'; 'claude-haiku-4-5-20251001' -> 'haiku-4-5';
// an OpenRouter slug drops the vendor: 'openai/gpt-5.2' -> 'gpt-5.2'.
// The full id always stays in the tooltip.
export const shortModel = (id) => String(id || '')
  .replace(/^[^/]+\//, '')
  .replace(/^claude-/, '')
  .replace(/-\d{8}$/, '');

export const isChatgptId = (id) => /^chatgpt\//.test(String(id || ''));
export const isOpenRouterId = (id) => /\//.test(String(id || '')) && !isChatgptId(id);

// Rates for OpenRouter models only — Claude runs on the subscription, so a
// price tag there would be fiction. Fetched once per page, never blocking: a
// picker renders fine with no prices at all.
let priceCache = null;
let priceInflight = null;
const priceListeners = new Set();

export function fetchPricing() {
  if (priceCache) return Promise.resolve(priceCache);
  if (!priceInflight) {
    priceInflight = fetch('/api/pipeline/openrouter/pricing', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        priceCache = (d && d.pricing) || {};
        priceListeners.forEach((fn) => fn(priceCache));
        return priceCache;
      })
      .catch(() => ({}))
      .finally(() => { priceInflight = null; });
  }
  return priceInflight;
}

export function usePricing() {
  const [prices, setPrices] = useState(priceCache);
  useEffect(() => {
    priceListeners.add(setPrices);
    if (!priceCache) fetchPricing().then((p) => setPrices(p));
    return () => { priceListeners.delete(setPrices); };
  }, []);
  return prices || {};
}

// '$0.19/$0.94' — input and output rates per million tokens. Sub-cent rates keep
// two decimals so a cheap model doesn't read as free.
const fmtRate = (v) => (v >= 10 ? `$${v.toFixed(0)}` : `$${v.toFixed(2)}`);
export const priceLabel = (p) => (p ? `${fmtRate(p.promptPerM)}/${fmtRate(p.completionPerM)}` : '');
const priceTitle = (p) => (p ? [
  `${fmtRate(p.promptPerM)} per 1M input tokens, ${fmtRate(p.completionPerM)} per 1M output`,
  p.provider ? `via ${p.provider}${p.tier && p.tier !== 'global' ? ` · ${p.tier} tier` : ''}` : null,
  p.exact ? null : 'server routing has no endpoint at this tier — this is the cheapest available instead',
].filter(Boolean).join('\n') : null);

const tagStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  fontSize: 10, lineHeight: '14px', padding: '1px 6px', borderRadius: 999,
  border: '1px solid var(--border,#ddd)', background: 'rgba(127,127,127,0.10)',
  color: 'var(--fg,#3c4043)', whiteSpace: 'nowrap', fontFamily: 'inherit',
};

// Read-only chip naming a model — for turns already generated and for
// surfaces nobody can switch (a non-Claude provider).
export const ModelTag = ({ model, k, models, prefix = '⌁', title, style }) => {
  const auto = useModels();
  const map = models || auto;
  const entry = k && map ? map.byKey[k] : null;
  const id = model || (entry && entry.model) || '';
  if (!id) return null;
  const envs = entry && Array.isArray(entry.env) ? entry.env : [];
  const hint = title || [
    entry ? `${entry.label}: ${id}` : id,
    envs.length ? `override: ${envs.join(' → ')}` : null,
  ].filter(Boolean).join('\n');
  return (
    <span style={{ ...tagStyle, ...style }} title={hint}>
      <span aria-hidden="true" style={{ opacity: 0.6 }}>{prefix}</span>{shortModel(id)}
    </span>
  );
};

const pickerStyle = {
  ...tagStyle,
  cursor: 'pointer', padding: '1px 4px', appearance: 'auto',
};

// The prompt a surface will run with — the current template, before any
// per-point data is filled in. Opened by clicking an agent's name.
export const PromptPeek = ({ entry, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const url = entry.node
    ? `/api/pipeline/prompt-template/${entry.node}`
    : (entry.file ? `/api/pipeline/prompt-file/${entry.file}` : null);

  useEffect(() => {
    if (!url) return undefined;
    let alive = true;
    fetch(url, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [url]);

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300,
  };
  const box = {
    background: 'var(--bg,#fff)', color: 'var(--fg,#111)', width: 'min(900px, 92vw)',
    maxHeight: '88vh', overflow: 'auto', borderRadius: 8, padding: 20,
    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
  };
  const pre = {
    whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: 12, lineHeight: 1.45, background: 'rgba(127,127,127,0.08)',
    border: '1px solid var(--border,#ddd)', borderRadius: 6, padding: 10, marginTop: 6,
  };

  return createPortal(
    <div style={overlay} onMouseDown={onClose}>
      <div style={box} onMouseDown={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <h3 style={{ margin: 0 }}>
            {entry.label} — prompt
            {data && data.version && <span style={{ fontSize: 12, opacity: 0.6 }}> · v{data.version}</span>}
          </h3>
          <button onClick={onClose}>✕</button>
        </div>
        <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
          Runs on <b>{entry.model}</b>. Current template — a real turn also carries this point's data.
        </div>
        {!url && <div style={{ marginTop: 10, opacity: 0.7 }}>This surface has no prompt of its own.</div>}
        {error && <div style={{ marginTop: 10, color: '#c5221f' }}>Could not load the prompt: {error}</div>}
        {!data && !error && url && <div style={{ marginTop: 10, opacity: 0.6 }}>Loading…</div>}
        {data && (
          <>
            <div style={{ marginTop: 12, fontWeight: 600, fontSize: 13 }}>SYSTEM</div>
            <div style={pre}>{data.system || '(empty)'}</div>
            {data.user && (
              <>
                <div style={{ marginTop: 12, fontWeight: 600, fontSize: 13 }}>USER (template)</div>
                <div style={pre}>{data.user}</div>
              </>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

// An agent's name as a button that opens its prompt. `text` overrides the
// caption for places where the agent is already named nearby.
export const AgentPromptLink = ({ entry, text, style }) => {
  const [open, setOpen] = useState(false);
  const clickable = !!(entry.node || entry.file);
  const caption = text || entry.label;
  if (!clickable) return <span style={{ fontSize: 10, opacity: 0.7, ...style }}>{caption}</span>;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={`Show the prompt ${entry.label} runs with`}
        style={{
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          fontSize: 10, fontFamily: 'inherit', color: 'var(--fg,#3c4043)',
          opacity: 0.75, textDecoration: 'underline dotted', ...style,
        }}
      >
        {caption}
      </button>
      {open && <PromptPeek entry={entry} onClose={() => setOpen(false)} />}
    </>
  );
};

// Prompt link by surface key, for controls that already name themselves.
export const PromptLink = ({ k, text = '📄 prompt', style }) => {
  const models = useModels();
  const entry = models && models.byKey[k];
  if (!entry) return null;
  return <AgentPromptLink entry={entry} text={text} style={style} />;
};

// Chip-sized dropdown: which model this control will run on. Saving is
// immediate and per account, so the next launch starts from the same choice.
// `showLabel` prefixes the agent's name (for rows listing several agents).
export const ModelPicker = ({ k, showLabel = false, disabled = false, style, onChanged }) => {
  const models = useModels();
  const prices = usePricing();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const entry = models && models.byKey[k];
  if (!entry) return null;
  if (!entry.selectable) return <ModelTag k={k} models={models} style={style} />;

  const change = async (value) => {
    setBusy(true); setError(null);
    try {
      await saveModelPick(k, value);
      if (onChanged) onChanged(value);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const CUSTOM_OR = '__openrouter_custom__';
  const or = models.openrouter || { options: [], keySet: false };
  // A custom slug saved earlier must stay visible in the list to stay selected.
  const orOptions = (entry.picked && isOpenRouterId(entry.picked) && !or.options.includes(entry.picked))
    ? [...or.options, entry.picked]
    : or.options;

  const onSelect = (value) => {
    if (value !== CUSTOM_OR) return change(value);
    const slug = window.prompt(
      'OpenRouter model slug (vendor/model, e.g. openai/gpt-5.2):',
      isOpenRouterId(entry.picked) ? entry.picked : ''
    );
    if (slug == null) return undefined;                 // cancelled — keep the current pick
    const trimmed = slug.trim();
    if (!/^[\w.\-]+\/[\w.\-]+(:[\w.\-]+)?$/.test(trimmed)) {
      setError(`Not a vendor/model slug: "${trimmed}"`);
      return undefined;
    }
    return change(trimmed);
  };

  const cg = models.chatgpt || { options: [], binSet: false };
  const cgOptions = (entry.picked && isChatgptId(entry.picked) && !cg.options.includes(entry.picked))
    ? [...cg.options, entry.picked]
    : cg.options;
  const orKeyNote = (isOpenRouterId(entry.model) && !or.keySet)
    ? '⚠ OPENROUTER_API_KEY is not set on the server — runs will fail'
    : (isChatgptId(entry.model) && !cg.binSet)
      ? '⚠ OpenCode is not installed on the server — runs will fail'
      : null;
  const price = prices[entry.model] || null;
  const title = [
    `${entry.label} runs on ${entry.model}`,
    entry.picked ? 'your choice — saved to your account' : `server default (${entry.serverDefault})`,
    entry.env && entry.env.length ? `env override: ${entry.env.join(' → ')}` : null,
    priceTitle(price),
    orKeyNote,
    error ? `could not save: ${error}` : null,
  ].filter(Boolean).join('\n');
  // Rates in the option text: choosing a model for an overnight run is a price
  // decision, and a tooltip you have to hover for doesn't inform that choice.
  const optionText = (m) => {
    const p = prices[m];
    return p ? `${shortModel(m)} · ${priceLabel(p)}` : shortModel(m);
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {showLabel && <AgentPromptLink entry={entry} />}
      <select
        value={entry.picked || ''}
        disabled={disabled || busy}
        onChange={(e) => onSelect(e.target.value)}
        title={title}
        aria-label={`Model for ${entry.label}`}
        style={{ ...pickerStyle, opacity: busy ? 0.6 : 1, borderStyle: entry.picked ? 'solid' : 'dashed', ...style }}
      >
        <option value="">default · {shortModel(entry.serverDefault)}</option>
        <optgroup label="Claude">
          {(models.options || []).map((m) => (
            <option key={m} value={m}>{shortModel(m)}</option>
          ))}
        </optgroup>
        <optgroup label="OpenRouter · $ per 1M in/out">
          {orOptions.map((m) => (
            <option key={m} value={m}>{optionText(m)}</option>
          ))}
          <option value={CUSTOM_OR}>other slug…</option>
        </optgroup>
        {cgOptions.length > 0 && (
          <optgroup label="ChatGPT · subscription (OpenCode)">
            {cgOptions.map((m) => (
              <option key={m} value={m}>{shortModel(m)}</option>
            ))}
          </optgroup>
        )}
      </select>
      {orKeyNote && <span style={{ fontSize: 10 }} title={orKeyNote}>🔑⚠</span>}
      {error && <span style={{ fontSize: 10, color: '#c5221f' }} title={error}>⚠</span>}
    </span>
  );
};

// A row of pickers, one per surface — for controls that fire several agents.
export const ModelTagRow = ({ keys, label, style }) => {
  const models = useModels();
  if (!models) return null;
  const entries = keys.map((k) => models.byKey[k]).filter(Boolean);
  if (!entries.length) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', ...style }}>
      {label && <span style={{ fontSize: 11, opacity: 0.7 }}>{label}</span>}
      {entries.map((e) => <ModelPicker key={e.key} k={e.key} showLabel />)}
    </span>
  );
};

// Full reference AND the place to set every surface at once.
export const ModelsLegend = ({ onClose }) => {
  const models = useModels();
  const prices = usePricing();
  const [resetting, setResetting] = useState(false);
  const list = (models && models.list) || [];
  const groups = [];
  list.forEach((e) => {
    const g = groups.find((x) => x.name === e.group);
    if (g) g.items.push(e); else groups.push({ name: e.group, items: [e] });
  });
  const pickedCount = list.filter((e) => e.picked).length;

  const resetAll = async () => {
    setResetting(true);
    try {
      for (const e of list.filter((x) => x.picked)) await saveModelPick(e.key, null);
    } catch (err) { /* the per-picker error state covers this */ } finally { setResetting(false); }
  };

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200,
  };
  const box = {
    background: 'var(--bg,#fff)', color: 'var(--fg,#111)', width: 'min(720px, 92vw)',
    maxHeight: '86vh', overflow: 'auto', borderRadius: 8, padding: 20,
    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
  };
  return createPortal(
    <div style={overlay} onMouseDown={onClose}>
      <div style={box} onMouseDown={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>⌁ Models</h3>
          <button onClick={onClose}>✕</button>
        </div>
        <p style={{ fontSize: 13, opacity: 0.8 }}>
          Your own choice per agent — saved to your account, applied to every generation you start.
          «default» follows the server ({(models && models.base) || '…'} unless the agent is pinned).
        </p>
        {/* The tier and host pin live in server config, so a price on this screen
            would otherwise look unexplained. State the routing plainly. */}
        {models && models.openrouter && models.openrouter.tier && (
          <p style={{ fontSize: 12, opacity: 0.8, border: '1px solid var(--border,#ddd)', borderRadius: 6, padding: '6px 9px' }}>
            ⚙ OpenRouter models run on the <b>{models.openrouter.tier}</b> tier
            {models.openrouter.pin ? <> via <b>{models.openrouter.pin}</b></> : null}
            {' '}— applied automatically, nothing to switch on here. Prices below already reflect it.
          </p>
        )}
        {pickedCount > 0 && (
          <button onClick={resetAll} disabled={resetting} style={{ fontSize: 12 }}>
            {resetting ? 'Resetting…' : `↺ Back to defaults (${pickedCount})`}
          </button>
        )}
        {!models && <div style={{ opacity: 0.6 }}>Loading…</div>}
        {groups.map((g) => (
          <div key={g.name} style={{ marginTop: 14 }}>
            <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.6 }}>{g.name}</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 4 }}>
              <tbody>
                {g.items.map((e) => (
                  <tr key={e.key} style={{ borderTop: '1px solid var(--border,#eee)' }}>
                    <td style={{ padding: '5px 6px 5px 0' }}>
                      <AgentPromptLink entry={e} style={{ fontSize: 13, opacity: 1 }} />
                    </td>
                    <td style={{ padding: '5px 6px' }}>
                      {e.selectable
                        ? <ModelPicker k={e.key} />
                        : <span style={{ fontFamily: 'monospace' }}>{e.model}</span>}
                    </td>
                    <td style={{ padding: '5px 6px', fontSize: 11, opacity: 0.75, whiteSpace: 'nowrap' }}>
                      {/* Only metered models carry a price; a subscription surface stays blank. */}
                      {prices[e.model] ? (
                        <span title={priceTitle(prices[e.model])}>{priceLabel(prices[e.model])}</span>
                      ) : (isOpenRouterId(e.model) ? '' : <span title="Runs on the Claude subscription, not billed per token">subscription</span>)}
                    </td>
                    <td style={{ padding: '5px 0', fontSize: 11, opacity: 0.6 }}>
                      {e.picked ? 'your choice' : (e.env && e.env.length ? e.env.join(' → ') : 'fixed in code')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>,
    document.body
  );
};

export default ModelTag;
